import googleAnalytics from '@analytics/google-analytics'
import googleTagManager from '@analytics/google-tag-manager'
import { Analytics as AnalyticsInstance } from 'analytics'
import posthog from 'posthog-js'

type AnalyticsUser = {
  _id: string
  profile?: {
    sign?: string
    email?: string
    phone?: string
    gdpr?: { marketingType?: string }
  }
}

type DataLayerUserEvent =
  | 'user_identified'
  | 'user_anonymous'
  | 'user_logged_out'

// Types for configuration callbacks
type GetUserCallback = () => AnalyticsUser | null | undefined
type GetRouteNameCallback = () => string | null | undefined
type GetInstanceNameCallback = () => string | undefined

// Configuration interface
interface AnalyticsConfig {
  getUser?: GetUserCallback
  getRouteName?: GetRouteNameCallback
  getInstanceName?: GetInstanceNameCallback
  onPageChange?: () => void
}

const Analytics = (function () {
  let _analytics: ReturnType<typeof AnalyticsInstance> | null = null
  let _settings: any = null
  let _config: AnalyticsConfig = {}
  let _lastIdentifiedUserId: string | null = null
  let _lastDataLayerUserKey: string | null = null
  let _lastDataLayerUserEvent: DataLayerUserEvent | null = null
  // true once setUser() ran with a resolved user state (logged in OR confirmed anonymous)
  let _isUserStateResolved = false

  /**
   * Load settings from environment variables
   * @private
   */
  const _loadSettings = function () {
    const enabled = import.meta.env.VITE_ANALYTICS_ENABLED === 'true'
    const debug = import.meta.env.VITE_ANALYTICS_DEBUG === 'true'
    const app = import.meta.env.VITE_ANALYTICS_APP
    const version = import.meta.env.VITE_ANALYTICS_VERSION
    const gaTrackingId = import.meta.env.VITE_ANALYTICS_GA_TRACKING_ID
    const gtmContainerId = import.meta.env.VITE_ANALYTICS_GTM_CONTAINER_ID

    return {
      enabled,
      debug,
      app,
      version,
      googleAnalytics: gaTrackingId ? { trackingId: gaTrackingId } : null,
      googleTagManager: gtmContainerId ? { containerId: gtmContainerId } : null,
    }
  }

  const _pushToDataLayer = function (payload: Record<string, unknown>) {
    const browserWindow = window as unknown as {
      dataLayer?: Record<string, unknown>[]
    }
    browserWindow.dataLayer = browserWindow.dataLayer || []
    browserWindow.dataLayer.push(payload)
  }

  /**
   * Push user state into GTM dataLayer for Meta pixel Advanced Matching
   * (cross-browser attribution, e.g. FB in-app browser → SMS login link in Safari).
   *
   * Meta pixel takes user data ONLY from its first `fbq('init')` — a later init with
   * the same pixel id is ignored ("Duplicate Pixel ID"). GTM therefore must not fire
   * the first pixel tag before the user state is known, so we push one of:
   * - `user_identified` (with user_id / email / phone) — logged-in user
   * - `user_anonymous` — user state resolved, nobody logged in
   * - `user_logged_out` — clears previously pushed values
   * GTM gates the PageView tag on (cookie consent AND user_identified|user_anonymous).
   *
   * PII is sent only with marketing consent. Pushes only on change — phone/consent
   * can arrive later for the same user _id.
   * @private
   */
  const _pushUserToDataLayerIfChanged = function (user: AnalyticsUser | null) {
    if (!_settings.googleTagManager) return

    if (!user) {
      // page()/track() can run while the user query is still loading — GTM must not
      // get "anonymous" before setUser() confirmed it, or the pixel inits without data.
      if (!_isUserStateResolved) return

      const event: DataLayerUserEvent =
        _lastDataLayerUserEvent === 'user_identified'
          ? 'user_logged_out'
          : 'user_anonymous'
      if (_lastDataLayerUserEvent === event) return
      _lastDataLayerUserKey = null
      _lastDataLayerUserEvent = event
      _pushToDataLayer({
        event,
        user_id: undefined,
        user_email: undefined,
        user_phone: undefined,
      })
      return
    }

    const hasMarketingConsent = user.profile?.gdpr?.marketingType !== 'refused'
    const email = user.profile?.email?.trim().toLowerCase()
    // Meta expects digits only incl. country code, e.g. 421912345678
    const phone = user.profile?.phone?.replace(/\D/g, '')

    const userData = {
      user_id: user._id,
      user_email: hasMarketingConsent && email ? email : undefined,
      user_phone: hasMarketingConsent && phone ? phone : undefined,
    }
    const key = JSON.stringify(userData)
    if (key === _lastDataLayerUserKey) return
    _lastDataLayerUserKey = key
    _lastDataLayerUserEvent = 'user_identified'
    _pushToDataLayer({ event: 'user_identified', ...userData })
  }

  /**
   * Identify user if changed
   * @private
   */
  const _identifyUserIfChanged = function () {
    if (!_analytics || !_settings || !_settings.enabled) return

    if (_config.getUser) {
      const user = _config.getUser()
      if (user && user._id !== _lastIdentifiedUserId) {
        if (_settings.debug) {
          console.log('Identifying user...', user._id)
        }
        _analytics.identify(user._id, { sign: user.profile?.sign })
        _lastIdentifiedUserId = user._id
      } else if (!user) {
        // User logged out, reset tracking
        _lastIdentifiedUserId = null
      }

      _pushUserToDataLayerIfChanged(user ?? null)
    }
  }

  /**
   * Track page change
   * @private
   */
  const _trackPageChange = function (routeName?: string) {
    if (_analytics && _settings && _settings.enabled) {
      // Identify user only if changed
      _identifyUserIfChanged()

      // Track page change
      _analytics.page()

      // If debug is enabled, print page changed info into console
      if (_settings.debug) {
        console.info(
          'Analytics: Tracking page changed: ' +
          (routeName || 'Missing route name!')
        )
      }
    } else if (!import.meta.env.PROD) {
      // Analytics nie je inicializované (VITE_ANALYTICS_ENABLED != true) — na dev/test bežný stav,
      // do error trackingu sa neposiela (Sentry warning event bol len šum).
      console.warn(
        'Analytics: Trying to track route change, but Analytics is not initialized!',
        { route: routeName }
      )
    }
  }

  /**
   * Track event
   * @param eventName
   * @param payload
   * @param options
   * @param callback
   */
  const _trackEvent = function (
    eventName: string,
    payload?: any,
    options?: any,
    callback?: (...params: any[]) => any
  ) {
    const _payload = payload ? { ...payload } : {}

    if (!_analytics || !_settings) {
      // Analytics nie je inicializované — na dev/test bežný stav, do error trackingu sa neposiela.
      if (!import.meta.env.PROD) {
        const routeName = _config.getRouteName
          ? _config.getRouteName()
          : window.location.pathname

        console.warn(
          'Analytics: Trying to track event ' +
          eventName +
          ', but Analytics is not initialized!',
          { route: routeName }
        )
      }

      return
    }

    // Add user information
    if (_config.getUser) {
      const user = _config.getUser()
      if (user) {
        _payload.userSign = user.profile?.sign
      }
    }

    // Identify user only if changed
    _identifyUserIfChanged()

    _analytics.track(eventName, _payload, options, callback).catch((ex) => {
      posthog.captureException(ex, { analytics_event_name: eventName })

      if (_settings.debug) {
        console.error(ex)
      }
    })

    if (_settings.debug) {
      console.info(
        'Analytics: tracked event ' +
        eventName +
        ' with event data ' +
        JSON.stringify(payload)
      )
    }
  }

  /**
   * Initialize Analytics measurements
   * @private
   */
  const _init = function (config?: AnalyticsConfig) {
    if (config) {
      _config = { ..._config, ...config }
    }

    // Load settings from environment variables
    _settings = _loadSettings()

    if (!_analytics && _settings.enabled) {
      const options: any = { plugins: [] }

      if (_settings.app) {
        options.app = _settings.app
      }

      if (_settings.version) {
        options.version = _settings.version
      }

      if (_settings.googleAnalytics && _settings.googleAnalytics.trackingId) {
        options.plugins.push(
          googleAnalytics({
            debug: _settings.debug,
            trackingId: _settings.googleAnalytics.trackingId,
          })
        )

        if (_settings.debug) {
          console.log('Initializing GA plugin...')
        }
      }

      if (
        _settings.googleTagManager &&
        _settings.googleTagManager.containerId
      ) {
        options.plugins.push(
          googleTagManager({
            debug: _settings.debug,
            containerId: _settings.googleTagManager.containerId,
          })
        )

        if (_settings.debug) {
          console.log('Initializing GTM plugin...')
        }
      }

      // Initialize Analytics
      _analytics = AnalyticsInstance(options)

      // Set up page change tracking via popstate and pushstate
      if (typeof window !== 'undefined') {
        // Track page changes on navigation
        const originalPushState = history.pushState
        const originalReplaceState = history.replaceState

        history.pushState = function (...args) {
          originalPushState.apply(history, args)
          if (_config.onPageChange) {
            _config.onPageChange()
          }
        }

        history.replaceState = function (...args) {
          originalReplaceState.apply(history, args)
          if (_config.onPageChange) {
            _config.onPageChange()
          }
        }

        window.addEventListener('popstate', () => {
          if (_config.onPageChange) {
            _config.onPageChange()
          }
        })
      }

      // Print initialization info into console, if debug is enabled
      if (_settings.debug) {
        console.info('Analytics: instance successfully initialized...')
      }
    }
  }

  return {
    init: (config?: AnalyticsConfig) => {
      _init(config)
    },
    setUser: (user: AnalyticsUser | null) => {
      if (_settings.debug) {
        console.log(
          user == null
            ? 'Removing user from analytics...'
            : 'Setting user in analytics...',
          user?._id
        )
      }

      _config.getUser = () => user
      // AnalyticsProvider calls setUser only after the user query settled (also for
      // anonymous = null), so from here on "no user" means confirmed anonymous.
      _isUserStateResolved = true
      // usera sme sice ulozili ale neindentifikovali .. cize pri logine a redirecte na / sme nevedeli kto je user, identifikoval ho az pri zmene routy page() a track eeventoch track()
      _identifyUserIfChanged()
    },
    track: (
      eventName: string,
      payload?: any,
      options?: any,
      callback?: (...params: any[]) => any
    ) => {
      _trackEvent(eventName, payload, options, callback)
    },
    on: (
      onEventName: string,
      onEventCallback: (...params: any[]) => any
    ): void => {
      if (_analytics) {
        _analytics.on(onEventName, onEventCallback)
      }
    },
    page: (pathname?: string) => {
      _trackPageChange(pathname)
    },
  }
})()

export default Analytics
